import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Logo from '../../Logo/Logo';
import useMeetingStart from '../../../hooks/useMeeting/useMeetingStart';
import WaitingCounter from './WaitingCounter';
import useMeetingParams from '../../../hooks/useMeeting/useMeetingParams';
import meetingService from '../../../services/meeting.service';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2rem',
      height: '100%',
      width: '100%',
      margin: '0 auto',
      padding: '4rem 1rem 1rem',
      textAlign: 'center',
      maxWidth: '500px',
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
    },
    divider: {
      width: '100%',
    },
    modalContainer: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1000,
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      borderRadius: '24px',
      boxShadow: '12px 12px 24px 0px #0000001F', // Sombra personalizada
    },
    modalContent: {
      width: '430px',
      height: '323px',
      padding: '32px',
      borderRadius: '24px',
      border: '1px solid #FBFBFB', // Cambia el color del borde
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
    },
    counterContainer: {
      width: '96px',
      height: '96px',
      padding: '16px',
      borderRadius: '32px',
      background: '#E9F3FD',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    counterContent: {
      width: '64px',
      height: '64px',
      borderRadius: '24px',
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    counter: {
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0px',
      textAlign: 'center',
    },
    title: {
      color: theme.colors['neutral-600'],
    },
    secondTitle: {
      fontFamily: 'Poppins',
      fontSize: '22px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0px',
      textAlign: 'center',
    },
    message: {
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0px',
      textAlign: 'center',
    },
    buttonContainer: {
      display: 'flex',
      gap: '8px',
    },
    button: {
      width: '260px',
      height: '48px',
      borderRadius: '12px',
      gap: '10px',
      textTransform: 'none',
    },
    endButton: {
      width: '79px',
      height: '48px',
      padding: '14px 24px 14px 24px',
      borderRadius: '12px',
      gap: '10px',
      background: '#E9F3FD',
      color: theme.colors['info-500'],
    },
    continueButton: {
      width: '173px',
      height: '48px',
      padding: '14px 24px 14px 24px',
      borderRadius: '12px',
      gap: '10px',
      background: '#0080EA',
      color: 'white',
      '&:hover': {
        backgroundColor: '#0070D0',
      },
    },
  })
);

const AUTO_CLOSE_TIMEOUT_SECONDS = 30;
const INACTIVITY_TIMEOUT_SECONDS = 600;
function generateUniqueId() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);

  let id = '';
  array.forEach(byte => {
    id += byte.toString(16).padStart(2, '0');
  });

  return id;
}
let autoCloseTimeout: NodeJS.Timeout;
let inactivityTimeout: NodeJS.Timeout;

function MeetingWaitingRoom() {
  const classes = useStyles();
  const { startsAt, isFetching } = useMeetingStart();
  const { sessionId } = useMeetingParams();

  const [showModal, setShowModal] = useState(false);
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(AUTO_CLOSE_TIMEOUT_SECONDS);
  const [isCounting, setIsCounting] = useState(false);
  const [uniqueId, setUniqueId] = useState(() => {
    const storedUniqueId = localStorage.getItem('uniqueId');
    if (storedUniqueId) {
      return storedUniqueId;
    } else {
      const newUniqueId = generateUniqueId();
      localStorage.setItem('uniqueId', newUniqueId);
      return newUniqueId;
    }
  });
  useEffect(() => {
    if (sessionId) {
      meetingService.updateState(sessionId, uniqueId);
    }
  }, [uniqueId]);
  const showModalHandler = () => {
    if (sessionId) {
      meetingService.updateStatePending(sessionId, uniqueId);
    }
    setShowModal(true);
    resetAutoCloseTimeout();
    setIsCounting(true);
  };

  const hideModalHandler = () => {
    setShowModal(false);
    if (sessionId) {
      meetingService.updateState(sessionId, uniqueId);
    }
    clearTimeout(autoCloseTimeout);
    clearTimeout(inactivityTimeout);
    setIsCounting(false);
  };

  const resetAutoCloseTimeout = () => {
    clearTimeout(autoCloseTimeout);
    setAutoCloseCountdown(AUTO_CLOSE_TIMEOUT_SECONDS);
    startAutoCloseTimeout();
  };

  const startAutoCloseTimeout = () => {
    clearTimeout(autoCloseTimeout);

    autoCloseTimeout = global.setTimeout(() => {
      if (showModal) {
        redirectToURL();
      }
    }, AUTO_CLOSE_TIMEOUT_SECONDS * 1000);

    const countdownInterval = setInterval(() => {
      setAutoCloseCountdown(autoCloseCountdown => {
        if (autoCloseCountdown > 0) {
          return autoCloseCountdown - 1;
        } else {
          clearInterval(countdownInterval);
          return 0;
        }
      });
    }, 1000);
  };

  const resetInactivityTimeout = () => {
    clearTimeout(inactivityTimeout);

    inactivityTimeout = global.setTimeout(() => {
      showModalHandler();
    }, INACTIVITY_TIMEOUT_SECONDS * 1000);
  };

  const redirectToURL = async () => {
    if (!sessionId) {
      return;
    }

    let redirectToPortal = false;
    const response = await meetingService.validatePortalPatient(sessionId);
    redirectToPortal = response.payload;

    const url = redirectToPortal
      ? 'https://portalpacientes.saludtools.com/'
      : 'https://saludtools.com/portal-pacientes';

    window.location.href = url;
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (sessionId) {
        meetingService.updateStatePending(sessionId, uniqueId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('mousemove', resetInactivityTimeout);
    document.addEventListener('keydown', resetInactivityTimeout);

    resetInactivityTimeout();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('mousemove', resetInactivityTimeout);
      document.removeEventListener('keydown', resetInactivityTimeout);
      clearTimeout(autoCloseTimeout);
      clearTimeout(inactivityTimeout);
    };
  }, []);

  useEffect(() => {
    if (autoCloseCountdown === 0 && showModal && isCounting) {
      redirectToURL();
    }
  }, [autoCloseCountdown, showModal, isCounting]);

  return (
    <div className={classes.container}>
      <Logo full size="large" />
      <svg width="196" height="160" viewBox="0 0 196 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          opacity="0.12"
          d="M160.929 75.0774H141.557C134.945 75.0774 129.591 69.6624 129.591 63.0512C129.591 56.7652 134.421 51.6126 140.561 51.1089H143.044C149.656 51.1089 155.009 45.7464 155.009 39.1247C155.009 32.5029 149.656 27.1404 143.044 27.1404H24.7131C18.1019 27.1299 12.748 32.4924 12.748 39.1142C12.748 45.7359 18.1019 51.0984 24.7131 51.0984H42.2208C48.371 51.6126 53.1906 56.7547 53.1906 63.0407C53.1906 69.2952 48.6225 74.5212 42.5351 75.0774C42.1894 75.0774 41.8436 75.0984 41.4979 75.1299C41.4874 75.1299 41.4769 75.1299 41.4664 75.1299C35.3791 75.6965 30.6224 80.8177 30.6224 87.0616C30.6224 93.2741 35.3372 98.3743 41.3721 98.9829L41.2254 99.0459C47.8366 99.0459 53.1906 104.408 53.1906 111.03C53.1906 117.652 47.8366 123.014 41.2254 123.014L41.4979 123.088H28.4431C21.8319 123.088 16.4779 128.45 16.4779 135.072C16.4779 141.694 21.8319 147.056 28.4431 147.056H146.774C153.385 147.056 158.739 141.694 158.739 135.072C158.739 128.45 153.385 123.088 146.774 123.088H142.479L142.751 123.014C136.14 123.014 130.786 117.652 130.786 111.03C130.786 104.408 136.14 99.0459 142.751 99.0459H160.929C167.54 99.0459 172.894 93.6834 172.894 87.0616C172.894 80.4399 167.54 75.0774 160.929 75.0774Z"
          fill="#CDCDCD"
        />
        <path
          d="M18.641 58.2357H16.0175V55.6033C16.0175 54.9025 15.4383 54.3213 14.7399 54.3213C14.0414 54.3213 13.4622 54.9025 13.4622 55.6033V58.2357H10.8387C10.1232 58.2357 9.561 58.8168 9.561 59.5176C9.561 60.2185 10.1402 60.7996 10.8387 60.7996H13.4622V63.432C13.4622 64.1328 14.0414 64.714 14.7399 64.714C15.4554 64.714 16.0175 64.1328 16.0175 63.432V60.7996H18.641C19.3395 60.7996 19.9187 60.2185 19.9187 59.5176C19.9187 58.7997 19.3395 58.2357 18.641 58.2357Z"
          fill="#EBEBEB"
        />
        <path
          d="M172.216 143.578H169.189V140.541C169.189 139.712 168.521 139.062 167.715 139.062C166.909 139.062 166.241 139.732 166.241 140.541V143.578H163.214C162.388 143.578 161.74 144.249 161.74 145.057C161.74 145.886 162.408 146.537 163.214 146.537H166.241V149.574C166.241 150.402 166.909 151.053 167.715 151.053C168.541 151.053 169.189 150.383 169.189 149.574V146.537H172.216C173.042 146.537 173.691 145.866 173.691 145.057C173.71 144.249 173.042 143.578 172.216 143.578Z"
          fill="#BEBEBE"
        />
        <path
          d="M109.972 12.2729H106.743V8.83055C106.743 7.91409 106.03 7.1541 105.171 7.1541C104.29 7.1541 103.598 7.91409 103.598 8.83055V12.2729H100.369C99.4887 12.2729 98.7968 13.0329 98.7968 13.9493C98.7968 14.8658 99.5096 15.6258 100.369 15.6258H103.598V19.0681C103.598 19.9846 104.311 20.7446 105.171 20.7446C106.051 20.7446 106.743 19.9846 106.743 19.0681V15.6258H109.972C110.853 15.6258 111.545 14.8658 111.545 13.9493C111.545 13.0105 110.853 12.2729 109.972 12.2729Z"
          fill="#DDDDDD"
        />
        <g clipPath="url(#clip0_2_57)">
          <path
            d="M137.18 124.68H128.586V115.982H140.618C141.568 115.982 142.337 115.204 142.337 114.243V107.285C142.337 106.324 141.568 105.545 140.618 105.545H138.899V93.3688C138.893 88.5678 135.049 84.6778 130.304 84.6713H123.429C118.684 84.6778 114.84 88.5678 114.834 93.3688V105.545H113.115C112.165 105.545 111.396 106.324 111.396 107.285V109.024H107.958V107.285C107.958 106.324 107.189 105.545 106.239 105.545H104.52V93.3688C104.514 88.5678 100.67 84.6778 95.9255 84.6713H89.0497C84.3053 84.6778 80.4613 88.5678 80.4549 93.3688V105.545H78.7359C77.7862 105.545 77.017 106.324 77.017 107.285V109.024H73.5791V107.285C73.5791 106.324 72.8099 105.545 71.8601 105.545H70.1412V93.3688C70.1347 88.5678 66.2907 84.6778 61.5464 84.6713H54.6706C49.9263 84.6778 46.0823 88.5678 46.0759 93.3688V105.545H44.3569C43.4072 105.545 42.638 106.324 42.638 107.285V114.243C42.638 115.204 43.4072 115.982 44.3569 115.982H56.3896V124.68H40.919C39.9693 124.68 39.2001 125.458 39.2001 126.419C39.2001 127.38 39.9693 128.159 40.919 128.159H137.18C138.13 128.159 138.899 127.38 138.899 126.419C138.899 125.458 138.13 124.68 137.18 124.68ZM118.272 93.3688C118.272 90.4878 120.582 88.1503 123.429 88.1503H130.304C133.151 88.1503 135.461 90.4878 135.461 93.3688V105.545H118.272V93.3688ZM114.834 109.024H138.899V112.503H114.834V109.024ZM83.8928 93.3688C83.8928 90.4878 86.2026 88.1503 89.0497 88.1503H95.9255C98.7725 88.1503 101.082 90.4878 101.082 93.3688V105.545H83.8928V93.3688ZM80.4549 109.024H104.52V112.503H80.4549V109.024ZM49.5138 93.3688C49.5138 90.4878 51.8236 88.1503 54.6706 88.1503H61.5464C64.3934 88.1503 66.7033 90.4878 66.7033 93.3688V105.545H49.5138V93.3688ZM46.0759 112.503V109.024H70.1412V112.503H46.0759ZM59.8275 124.68V115.982H71.8601C72.8099 115.982 73.5791 115.204 73.5791 114.243V112.503H77.017V114.243C77.017 115.204 77.7862 115.982 78.7359 115.982H90.7686V124.68H59.8275ZM94.2065 124.68V115.982H106.239C107.189 115.982 107.958 115.204 107.958 114.243V112.503H111.396V114.243C111.396 115.204 112.165 115.982 113.115 115.982H125.148V124.68H94.2065Z"
            fill="#666666"
            fillOpacity="0.8"
          />
          <path
            d="M144.056 124.68H142.337C141.387 124.68 140.618 125.458 140.618 126.419C140.618 127.38 141.387 128.159 142.337 128.159H144.056C145.006 128.159 145.775 127.38 145.775 126.419C145.775 125.458 145.006 124.68 144.056 124.68Z"
            fill="#666666"
            fillOpacity="0.8"
          />
          <path
            d="M111.396 75.9736C120.889 75.9736 128.585 68.185 128.585 58.5786C128.585 48.9722 120.889 41.1836 111.396 41.1836C101.903 41.1836 94.2065 48.9722 94.2065 58.5786C94.2172 68.1807 101.907 75.9627 111.396 75.9736ZM111.396 44.6626C118.992 44.6626 125.148 50.8922 125.148 58.5786C125.148 66.265 118.992 72.4946 111.396 72.4946C103.8 72.4946 97.6444 66.265 97.6444 58.5786C97.653 50.8966 103.805 44.6713 111.396 44.6626Z"
            fill="#666666"
            fillOpacity="0.8"
          />
          <path
            d="M110.852 60.2268L116.009 61.9663C116.912 62.2707 117.885 61.7771 118.186 60.8639C118.487 59.9506 117.999 58.9656 117.096 58.6612L113.115 57.324V49.8811C113.115 48.92 112.346 48.1416 111.396 48.1416C110.446 48.1416 109.677 48.92 109.677 49.8811V58.5786C109.677 59.3266 110.152 59.9919 110.852 60.2268Z"
            fill="#666666"
            fillOpacity="0.8"
          />
        </g>
        <defs>
          <clipPath id="clip0_2_57">
            <rect width="106.575" height="86.975" fill="white" transform="translate(39.2001 41.1836)" />
          </clipPath>
        </defs>
      </svg>
      <Typography variant="h2" className={classes.title}>
        ¡Ya casi es tu cita! Solo faltan!
      </Typography>
      <WaitingCounter startsAt={startsAt} isFetching={isFetching} />
      <Typography>
        Accederás automáticamente a la consulta una vez el profesional de la salud haya ingresado a la sala. Por favor
        espera.
      </Typography>
      {showModal && (
        <>
          <div className={classes.backdrop}></div>
          <div className={classes.modalContainer}>
            <div className={classes.modalContent}>
              <div className={classes.counterContainer}>
                <div className={classes.counterContent}>
                  <Typography className={classes.counter}>00:{autoCloseCountdown}</Typography>
                </div>
              </div>
              <Typography variant="h2" className={classes.secondTitle}>
                ¿Sigues ahí?
              </Typography>
              <Typography className={classes.message}>
                Has estado esperando bastante tiempo. ¿Quieres seguir esperando para unirte?
              </Typography>
              <div className={classes.buttonContainer}>
                <Button className={`${classes.button} ${classes.endButton}`} onClick={() => redirectToURL()}>
                  Salir
                </Button>
                <Button className={`${classes.button} ${classes.continueButton}`} onClick={hideModalHandler}>
                  Seguir esperando
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MeetingWaitingRoom;
